// v6 F15.3 Sprint B — Prijslijst Excel import/export dialog.
// UI-flow:
//   1) "Import / Export" knop opent een menu-dialog met 3 acties:
//      - Exporteer .xlsx (directe download van huidige prijslijst incl. inactieve)
//      - Voorbeeldbestand (download lege template met demo-regels)
//      - Importeer bestand (opent 2e dialog met preview + apply)
//   2) Bij "Importeer" kiest de gebruiker een .xlsx → preview endpoint wordt
//      aangeroepen; resultaat toont # new/updated/errors/duplicates.
//   3) Gebruiker kiest mode ("Alleen toevoegen" of "Toevoegen + bijwerken")
//      en bevestigt → apply endpoint schrijft naar DB. Fout of dubbel
//      = geen mutatie (fail-fast op backend).
import React, { useRef, useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, RotateCw } from "lucide-react";
import {
  exportPriceCodesXlsxUrl, importTemplateXlsxUrl,
  previewPriceCodesImport, applyPriceCodesImport,
} from "../../lib/api";
import { toast } from "sonner";

export default function PriceListExcelDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("menu"); // menu | previewing | preview | applying
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("add_only");
  const fileRef = useRef(null);

  const reset = () => { setStep("menu"); setFile(null); setPreview(null); setMode("add_only"); };
  const close = () => { reset(); onOpenChange(false); };

  const downloadWithAuth = async (url, filename) => {
    // Onze axios-instance zet de Authorization-header automatisch; via anchor-tag
    // krijg je die niet. Gebruik fetch met token uit localStorage of doe via
    // axios met responseType blob.
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(`Download mislukt: ${e.message}`);
    }
  };

  const onExport = () =>
    downloadWithAuth(exportPriceCodesXlsxUrl(), `EmmaMeasure_prijslijst_${new Date().toISOString().slice(0,10)}.xlsx`);
  const onTemplate = () =>
    downloadWithAuth(importTemplateXlsxUrl(), "EmmaMeasure_prijslijst_voorbeeld.xlsx");

  const onFileChosen = async (f) => {
    setFile(f);
    setStep("previewing");
    try {
      const p = await previewPriceCodesImport(f);
      setPreview(p);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Preview mislukt");
      reset();
    }
  };

  const onApply = async () => {
    setStep("applying");
    try {
      const r = await applyPriceCodesImport(file, mode);
      toast.success(`Import voltooid: +${r.added} nieuw · ~${r.updated} bijgewerkt`);
      onImported?.();
      close();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail || "Import mislukt");
      setStep("preview");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl" data-testid="pricelist-excel-dialog">
        {step === "menu" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#D96C4A]" /> Excel Import / Export
              </DialogTitle>
              <DialogDescription>
                Beheer je prijslijst als .xlsx-bestand. Handig voor back-up of om een bestaande prijslijst te importeren.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
              <button
                type="button"
                onClick={onExport}
                className="border border-[#E5E2DC] rounded-xl p-4 text-left hover:border-[#D96C4A] hover:bg-[#FFF8F2] transition"
                data-testid="prc-export-btn"
              >
                <Download className="w-5 h-5 text-[#D96C4A] mb-2" />
                <div className="font-semibold text-sm text-[#2C2C2A]">Exporteer .xlsx</div>
                <div className="text-xs text-[#4F4F4D] mt-1">Downloadt de volledige prijslijst (ook inactieve regels).</div>
              </button>
              <button
                type="button"
                onClick={onTemplate}
                className="border border-[#E5E2DC] rounded-xl p-4 text-left hover:border-[#D96C4A] hover:bg-[#FFF8F2] transition"
                data-testid="prc-template-btn"
              >
                <FileSpreadsheet className="w-5 h-5 text-[#5D7259] mb-2" />
                <div className="font-semibold text-sm text-[#2C2C2A]">Voorbeeldbestand</div>
                <div className="text-xs text-[#4F4F4D] mt-1">Leeg .xlsx met kolomkoppen + 3 demoregels.</div>
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="border border-[#E5E2DC] rounded-xl p-4 text-left hover:border-[#D96C4A] hover:bg-[#FFF8F2] transition"
                data-testid="prc-import-btn"
              >
                <Upload className="w-5 h-5 text-[#B07A4F] mb-2" />
                <div className="font-semibold text-sm text-[#2C2C2A]">Importeer bestand</div>
                <div className="text-xs text-[#4F4F4D] mt-1">Kies een .xlsx — je krijgt eerst een preview.</div>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                data-testid="prc-file-input"
                onChange={(e) => e.target.files?.[0] && onFileChosen(e.target.files[0])}
              />
            </div>
          </>
        )}

        {step === "previewing" && (
          <div className="py-8 text-center text-[#4F4F4D]">
            <RotateCw className="w-6 h-6 mx-auto animate-spin text-[#D96C4A] mb-3" />
            Bestand wordt geanalyseerd…
          </div>
        )}

        {step === "preview" && preview && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#D96C4A]" /> Import-voorbeeld
              </DialogTitle>
              <DialogDescription>
                <span className="font-medium">{file?.name}</span> · nog geen wijzigingen doorgevoerd.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard color="#3F7A4D" label="Nieuw" value={preview.new.length} tid="prc-preview-new" />
              <StatCard color="#B07A4F" label="Gewijzigd" value={preview.updated.length} tid="prc-preview-updated" />
              <StatCard color="#4F4F4D" label="Ongewijzigd" value={preview.unchanged_count} tid="prc-preview-unchanged" />
              <StatCard color="#9C2A1A" label="Fouten" value={preview.errors.length} tid="prc-preview-errors" />
            </div>

            {preview.duplicates_in_file.length > 0 && (
              <div className="mt-3 border border-[#F1C7BD] bg-[#FBE9E7] rounded-lg p-3 text-sm text-[#9C2A1A]" data-testid="prc-preview-duplicates">
                <AlertCircle className="inline w-4 h-4 mr-1" />
                <strong>Dubbele codes in bestand:</strong> {preview.duplicates_in_file.join(", ")}. Herstel eerst en probeer opnieuw.
              </div>
            )}

            {preview.errors.length > 0 && (
              <details className="mt-3 border border-[#F1C7BD] bg-[#FBE9E7] rounded-lg p-3 text-sm" data-testid="prc-preview-errors-list">
                <summary className="cursor-pointer text-[#9C2A1A] font-semibold">
                  {preview.errors.length} validatiefout{preview.errors.length > 1 ? "en" : ""}
                </summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-auto">
                  {preview.errors.slice(0, 20).map((e, i) => (
                    <li key={i} className="text-[#9C2A1A]">Rij {e.row}: {e.message}</li>
                  ))}
                  {preview.errors.length > 20 && (
                    <li className="text-[#4F4F4D] italic">…nog {preview.errors.length - 20} meer</li>
                  )}
                </ul>
              </details>
            )}

            {preview.new.length > 0 && (
              <details className="mt-2 border border-[#C7DECB] bg-[#E8F2EA] rounded-lg p-3 text-sm">
                <summary className="cursor-pointer text-[#3F7A4D] font-semibold">
                  {preview.new.length} nieuwe regel{preview.new.length !== 1 ? "s" : ""}
                </summary>
                <ul className="mt-2 space-y-0.5 max-h-40 overflow-auto text-[#2C2C2A]">
                  {preview.new.slice(0, 20).map((r, i) => (
                    <li key={i}><strong>{r.code}</strong> · {r.description} · € {(+r.default_price).toFixed(2)}</li>
                  ))}
                </ul>
              </details>
            )}

            {preview.updated.length > 0 && (
              <details className="mt-2 border border-[#F5D6B2] bg-[#FFF4E6] rounded-lg p-3 text-sm">
                <summary className="cursor-pointer text-[#A85B1F] font-semibold">
                  {preview.updated.length} gewijzigde regel{preview.updated.length !== 1 ? "s" : ""}
                </summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-auto text-[#2C2C2A]">
                  {preview.updated.slice(0, 20).map((r, i) => (
                    <li key={i}>
                      <strong>{r.code}</strong> · {Object.keys(r.diffs).join(", ")}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="mt-4 border-t border-[#E5E2DC] pt-4 space-y-3">
              <Label className="text-xs uppercase tracking-wider font-semibold text-[#5D7259]">
                Wat wil je doen?
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { v: "add_only",       label: "Alleen toevoegen",        hint: "Bestaande codes blijven ongewijzigd" },
                  { v: "add_and_update", label: "Toevoegen + bijwerken",   hint: "Overschrijft bestaande codes met nieuwe waarden" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setMode(o.v)}
                    className={`border rounded-lg p-3 text-left transition ${
                      mode === o.v ? "border-[#D96C4A] bg-[#FFF8F2]" : "border-[#E5E2DC] hover:border-[#B07A4F]"
                    }`}
                    data-testid={`prc-mode-${o.v}`}
                  >
                    <div className="font-semibold text-sm text-[#2C2C2A]">{o.label}</div>
                    <div className="text-xs text-[#4F4F4D] mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={reset} data-testid="prc-cancel-preview">Annuleer</Button>
              <Button
                onClick={onApply}
                disabled={preview.errors.length > 0 || preview.duplicates_in_file.length > 0}
                className="bg-[#D96C4A] hover:bg-[#C05A3C]"
                data-testid="prc-apply-import"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Bevestig import
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "applying" && (
          <div className="py-8 text-center text-[#4F4F4D]">
            <RotateCw className="w-6 h-6 mx-auto animate-spin text-[#D96C4A] mb-3" />
            Import wordt toegepast…
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ color, label, value, tid }) {
  return (
    <div
      className="border rounded-lg p-3 text-center"
      style={{ borderColor: `${color}33`, backgroundColor: `${color}0d` }}
      data-testid={tid}
    >
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color }}>{label}</div>
    </div>
  );
}
